/**
 * MODULE: domain/pages/frontend/utils/pageBookmarkUtils.ts
 * PURPOSE: URL validation, embed detection, and display helpers for bookmark blocks
 */

export type BookmarkDisplayMode = 'card' | 'embed';

export type BookmarkEmbedProvider = 'youtube' | 'vimeo' | 'loom';

export type BookmarkBlockContent = {
  url: string;
  title: string;
  description?: string | null;
  image_url?: string | null;
  favicon_url?: string | null;
  display?: BookmarkDisplayMode | null;
};

const HTTP_PROTOCOL = /^https?:\/\//i;

export function normalizeBookmarkUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !HTTP_PROTOCOL.test(trimmed)) {
    return null;
  }
  const withProtocol = HTTP_PROTOCOL.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

export function bookmarkDisplayDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function defaultBookmarkTitle(url: string): string {
  const domain = bookmarkDisplayDomain(url);
  return domain || 'Bookmark';
}

export function bookmarkFaviconUrl(url: string): string {
  const domain = bookmarkDisplayDomain(url);
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
}

export function detectBookmarkEmbedProvider(url: string): BookmarkEmbedProvider | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    if (host === 'youtu.be' || host.endsWith('youtube.com')) {
      return 'youtube';
    }
    if (host === 'vimeo.com' || host.endsWith('.vimeo.com')) {
      return 'vimeo';
    }
    if (host === 'loom.com' || host.endsWith('.loom.com')) {
      return 'loom';
    }
    return null;
  } catch {
    return null;
  }
}

export function bookmarkSupportsEmbed(url: string): boolean {
  return detectBookmarkEmbedProvider(url) != null;
}

export function buildBookmarkEmbedUrl(url: string): string | null {
  const provider = detectBookmarkEmbedProvider(url);
  if (!provider) {
    return null;
  }
  try {
    const parsed = new URL(url);
    if (provider === 'youtube') {
      let videoId: string | null = null;
      if (parsed.hostname.replace(/^www\./, '').toLowerCase() === 'youtu.be') {
        videoId = parsed.pathname.replace(/^\//, '').split('/')[0] ?? null;
      } else {
        videoId = parsed.searchParams.get('v');
        if (!videoId && parsed.pathname.startsWith('/embed/')) {
          videoId = parsed.pathname.split('/')[2] ?? null;
        }
        if (!videoId && parsed.pathname.startsWith('/shorts/')) {
          videoId = parsed.pathname.split('/')[2] ?? null;
        }
      }
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    if (provider === 'vimeo') {
      const segments = parsed.pathname.split('/').filter(Boolean);
      const id = segments.find((segment) => /^\d+$/.test(segment));
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    if (provider === 'loom') {
      const shareMatch = parsed.pathname.match(/\/share\/([a-zA-Z0-9]+)/);
      const embedMatch = parsed.pathname.match(/\/embed\/([a-zA-Z0-9]+)/);
      const id = embedMatch?.[1] ?? shareMatch?.[1] ?? null;
      return id ? `https://www.loom.com/embed/${id}` : null;
    }
    return null;
  } catch {
    return null;
  }
}

export function sanitizeBookmarkDisplay(
  display: string | null | undefined,
  url: string
): BookmarkDisplayMode {
  if (display === 'embed' && bookmarkSupportsEmbed(url) && buildBookmarkEmbedUrl(url)) {
    return 'embed';
  }
  return 'card';
}

export function buildBookmarkBlockContent(input: {
  url: string;
  title?: string;
  description?: string;
  display?: BookmarkDisplayMode;
}): BookmarkBlockContent | null {
  const url = normalizeBookmarkUrl(input.url);
  if (!url) {
    return null;
  }
  const title = input.title?.trim() || defaultBookmarkTitle(url);
  const description = input.description?.trim() || null;
  return {
    url,
    title,
    description,
    image_url: null,
    favicon_url: bookmarkFaviconUrl(url),
    display: sanitizeBookmarkDisplay(input.display, url),
  };
}

export function shouldRenderBookmarkEmbed(
  content: BookmarkBlockContent,
  isPublicView: boolean
): boolean {
  if (isPublicView) {
    return false;
  }
  return sanitizeBookmarkDisplay(content.display ?? 'card', content.url) === 'embed';
}
